import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import axios from 'axios';
import { useFormik } from 'formik';
import * as yup from 'yup';
import "./SignOut.scss";
import { UcThemeContext } from '../../lib/UcThemeProvider';
import { UrlHelpers } from '../../lib/Helpers';
import { getUrl, domain } from '../../domain';
import { confirmPasswordSchema, passwordSchema } from '../../lib/PasswordValidation';

export default function Register(props) {
    // eslint-disable-next-line no-unused-vars
    const [themeState, themeDispatch] = React.useContext(UcThemeContext);
    const [openConfirmation, setOpenConfirmation] = React.useState(false);
    const [isSuccess, setIsSuccess] = React.useState(false);
    const [errors, setErrors] = React.useState([]);
    const [isSent, setIsSent] = React.useState(false);
    const [isEmailVerified, setIsEmailVerified] = React.useState(false);
    const [resendVerificationCode, setResendVerificationCode] = React.useState(false);
    const params = new URLSearchParams(window.location.search);
    const returnUrlParam = getUrl(UrlHelpers.getSafeUrl(params.get('ReturnUrl') || "", "/account/logout"));
    let emailParam = decodeURI(params.get('Email'));

    const handleCloseConfirmation = (event, reason) => {
      if (reason === 'clickaway') {
        return;
      }

      setOpenConfirmation(false);
    };

    const axiosconfig = {
        headers: {
            "Content-Type": "application/json",
            "SubDomain": domain.subDomain
        },
    };

    if (emailParam === 'null') {
        emailParam = null;
    }

    const initialValues = {
        email: emailParam !== null ? emailParam : '',
        password: '',
        confirmPassword: '',
        nickname: '' // honeypot field
    };
    const validationSchema = yup.object({
        email: yup
            .string('Enter your email')
            .email('Enter a valid email')
            .required('Email is required'),
        password: passwordSchema(),
        confirmPassword: confirmPasswordSchema(),
    });

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: (values) => {
            const returnUrlEncoded = encodeURIComponent(returnUrlParam);
            axios.post(getUrl(`/identity/account/register?ReturnUrl=${returnUrlEncoded}&Resend=${resendVerificationCode}`), JSON.stringify(values), axiosconfig)
                .then(response => {
                    formik.setSubmitting(false);

                    if (response.status === 200) {
                        setOpenConfirmation(true);
                        if (returnUrlParam.toLowerCase().startsWith("/account/logout"))
                        {
                            window.location = getUrl(returnUrlParam);
                        }
                        else
                        {
                            setIsSuccess(true);
                            // If user went through 2FA verification, mark email as verified
                            if (isSent) {
                                setIsEmailVerified(true);
                            }
                        }
                    }
                    else  {
                        setErrors(response);
                    }
                })
                .catch(error => {
                    if (error.response.status === 307) {
                        setIsSent(true);
                    }
                    else {
                        setErrors(error.response.data.errors);
                    }

                    formik.setSubmitting(false);
                });

                setResendVerificationCode(false);
        },
    });

    return (
        <>
            { !isSuccess && (
                <>
                    <Typography variant="subtitle1" gutterBottom component="div" sx={{mt:2}}>
                        Create a new account.
                    </Typography>
                    <form onSubmit={formik.handleSubmit}
                    >
                        <Grid container spacing={2}>
                            {
                                errors?.map((error) => (
                                    <Grid item xs={12} sm={12} md={12}>
                                        <Alert sx={{ mt: 2 }} severity="error">{error.description}</Alert>
                                    </Grid>
                                ))
                            }
                            <Grid item xs={12} sm={12} md={12}>
                                <TextField
                                    type="email"
                                    name="email"
                                    label="Email Address"
                                    fullWidth
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                    disabled={emailParam !== null ? true : false}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <TextField
                                    type="password"
                                    name="password"
                                    label="Password"
                                    fullWidth
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                    error={formik.touched.password && Boolean(formik.errors.password)}
                                    helperText={formik.touched.password && formik.errors.password}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <TextField
                                    type="password"
                                    name="confirmPassword"
                                    label="Confirm Password"
                                    fullWidth
                                    value={formik.values.confirmPassword}
                                    onChange={formik.handleChange}
                                    error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                                    helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                                />
                            </Grid>
                            { isSent && (
                                <>
                                    <Grid item xs={12} sm={12} md={12}>
                                        <Alert sx={{ mt: 2 }} severity="info">
                                            Please enter the verification code sent to the email or phone number you provided.
                                            <div style={{textAlign: 'right'}}>
                                                <Button size='small' sx={{mt: 1}}
                                                    variant='contained'
                                                    onClick={() => {
                                                    setResendVerificationCode(true);
                                                    formik.handleSubmit();
                                                }}>
                                                    resend
                                                </Button>
                                            </div>
                                        </Alert>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12}>
                                        <TextField
                                            type="text"
                                            name="code"
                                            label="Verification Code"
                                            fullWidth
                                            value={formik.values.code}
                                            onChange={formik.handleChange}
                                            error={formik.touched.code && Boolean(formik.errors.code)}
                                            helperText={formik.touched.code && formik.errors.code}
                                        />
                                    </Grid>
                                </>
                            )}
                            <TextField
                                type="text"
                                name="nickname"
                                value={formik.values.code}
                                onChange={formik.handleChange}
                                style={{ display: "none" }}
                            />
                            <Grid item md={12}>
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={formik.isSubmitting}
                                >
                                    Register
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                    <Grid container sx={{ mt: 1 }}>
                        <Grid item>
                            <Link href={getUrl("/account/login?ReturnUrl=" + encodeURIComponent(returnUrlParam))} variant="body2">
                                {"Already have an account? Sign In"}
                            </Link>
                        </Grid>
                    </Grid>
                </>
            )}
            {isSuccess && returnUrlParam !== "/account/logout" && !isEmailVerified && (
                <Alert severity="success" sx={{mt:2}}>
                     <Typography variant="body2" gutterBottom component="div">
                        An email has been sent to you.
                    </Typography>
                    <Typography variant="body2" gutterBottom component="div">
                        Please check your inbox to continue. You can <Link href={returnUrlParam}>click here to return</Link>.
                    </Typography>
                </Alert>
            )}
            {isSuccess && returnUrlParam !== "/account/logout" && isEmailVerified && (
                <Alert severity="success" sx={{mt:2}}>
                     <Typography variant="body2" gutterBottom component="div">
                        Your email has been confirmed and your account is ready.
                    </Typography>
                    <Typography variant="body2" gutterBottom component="div">
                        You can <Link href={returnUrlParam}>click here to log in</Link>.
                    </Typography>
                </Alert>
            )}
            {returnUrlParam === "/account/logout" && (
                <Snackbar
                    open={openConfirmation}
                    autoHideDuration={6000}
                    onClose={handleCloseConfirmation}
                >
                    <Alert severity="success">
                        Your account was created successfully.
                    </Alert>
                </Snackbar>
            )}
        </>
    );
}